import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  fetchAllSupportTickets,
  fetchSupportTicketDetails,
  updateSupportTicketStatus,
  replyToSupportTicket,
  type SupportTicket,
  type SupportTicketDetail,
} from '../../services/superadmin';
import {
  MessageCircle,
  Eye,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Filter,
  Search,
} from 'lucide-react';

const SuperAdminSupport: React.FC = () => {
  const [allTickets, setAllTickets] = useState<SupportTicket[]>([]);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketDetail | null>(null);
  const [_loadingTicket, setLoadingTicket] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);


  // Debounce search term to prevent excessive filtering
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Filter tickets from allTickets based on debounced search term
  const applySearchFilter = useCallback(() => {
    if (!debouncedSearchTerm.trim()) {
      setTickets(allTickets);
      return;
    }

    const filtered = allTickets.filter(
      (t) =>
        t.subject.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        t.user_name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        t.user_email.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
    setTickets(filtered);
  }, [allTickets, debouncedSearchTerm]);

  // Apply search filter when debounced search term or allTickets changes
  useEffect(() => {
    applySearchFilter();
  }, [applySearchFilter]);

  // Fetch tickets when status filter changes
  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  // Memoize onChange handler to prevent input recreation
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await fetchAllSupportTickets(params);
      if (response.data.success) {
        const ticketsResponse = response.data.tickets;
        setAllTickets(ticketsResponse);
        // Search filter will be applied by the applySearchFilter effect
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId: number) => {
    setLoadingTicket(true);
    try {
      const response = await fetchSupportTicketDetails(ticketId);
      if (response.data.success) {
        setSelectedTicket({
          ticket: response.data.ticket,
          replies: response.data.replies || [],
        });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load ticket details');
    } finally {
      setLoadingTicket(false);
    }
  };

  const handleStatusUpdate = async (ticketId: number, newStatus: string) => {
    try {
      const response = await updateSupportTicketStatus(ticketId, newStatus);
      if (response.data.success) {
        toast.success('Ticket status updated');
        await fetchTickets();
        if (selectedTicket && selectedTicket.ticket.id === ticketId) {
          await fetchTicketDetails(ticketId);
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleReply = async (ticketId: number) => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setReplying(true);
    try {
      const response = await replyToSupportTicket(ticketId, replyMessage);
      if (response.data.success) {
        toast.success('Reply sent successfully');
        setReplyMessage('');
        await fetchTicketDetails(ticketId);
        await fetchTickets();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'text-blue-600 bg-blue-100';
      case 'in_progress':
        return 'text-yellow-600 bg-yellow-100';
      case 'resolved':
        return 'text-green-600 bg-green-100';
      case 'closed':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="h-4 w-4" />;
      case 'in_progress':
        return <Clock className="h-4 w-4" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4" />;
      case 'closed':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-secondary-600">Loading tickets...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-secondary-900">Support Ticket Management</h2>
          <p className="text-sm text-secondary-600 mt-1">
            Manage and respond to all support tickets
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400 pointer-events-none z-10" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by subject, user name, or email..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="input-field !pl-10 pr-3 w-full"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-secondary-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-secondary-200">
          <thead className="bg-secondary-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Ticket ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Subject
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Mobile
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Updated
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-secondary-200">
            {tickets.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-secondary-500">
                  <MessageCircle className="h-12 w-12 mx-auto mb-4 text-secondary-300" />
                  <p>No tickets found</p>
                </td>
              </tr>
            ) : (
              tickets.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-secondary-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                    #{ticket.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-secondary-900">{ticket.user_name}</div>
                    <div className="text-sm text-secondary-500">{ticket.user_email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-secondary-900">{ticket.subject}</div>
                    {ticket.reply_count > 0 && (
                      <div className="text-xs text-secondary-500 mt-1">
                        {ticket.reply_count} {ticket.reply_count === 1 ? 'reply' : 'replies'}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                    {ticket.mobile_number || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        ticket.status
                      )}`}
                    >
                      {getStatusIcon(ticket.status)}
                      <span className="ml-1 capitalize">{ticket.status.replace('_', ' ')}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                    {new Date(ticket.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                    {new Date(ticket.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => fetchTicketDetails(ticket.id)}
                      className="text-primary-600 hover:text-primary-900 flex items-center gap-1"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedTicket(null)}
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-secondary-900">
                Ticket #{selectedTicket.ticket.id}
              </h2>
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="bg-secondary-50 p-4 rounded-lg">
                <h3 className="font-semibold text-secondary-900 mb-2">User Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-secondary-500">Name:</span>
                    <span className="ml-2 text-secondary-900">{selectedTicket.ticket.user_name}</span>
                  </div>
                  <div>
                    <span className="text-secondary-500">Email:</span>
                    <span className="ml-2 text-secondary-900">{selectedTicket.ticket.user_email}</span>
                  </div>
                  {selectedTicket.ticket.mobile_number && (
                    <div>
                      <span className="text-secondary-500">Mobile:</span>
                      <span className="ml-2 text-secondary-900">
                        {selectedTicket.ticket.mobile_number}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Ticket Info */}
              <div className="border-b border-secondary-200 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-secondary-900">
                    {selectedTicket.ticket.subject}
                  </h3>
                  <div className="flex items-center gap-3">
                    <select
                      value={selectedTicket.ticket.status}
                      onChange={(e) =>
                        handleStatusUpdate(selectedTicket.ticket.id, e.target.value)
                      }
                      className="input-field text-sm"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
                <p className="text-sm text-secondary-600">
                  Created: {new Date(selectedTicket.ticket.created_at).toLocaleString()}
                </p>
              </div>

              {/* Original Message */}
              <div className="bg-secondary-50 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-secondary-900">User Message</span>
                      <span className="text-xs text-secondary-500">
                        {new Date(selectedTicket.ticket.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-secondary-700 whitespace-pre-wrap">
                      {selectedTicket.ticket.message}
                    </p>
                  </div>
                </div>
              </div>

              {/* Replies */}
              {selectedTicket.replies.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-secondary-900">Conversation</h4>
                  {selectedTicket.replies.map((reply) => (
                    <div
                      key={reply.id}
                      className={`p-4 rounded-lg ${
                        reply.sender === 'admin'
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : 'bg-secondary-50 border-l-4 border-secondary-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`text-sm font-semibold ${
                            reply.sender === 'admin' ? 'text-blue-900' : 'text-secondary-900'
                          }`}
                        >
                          {reply.sender === 'admin' ? 'Support Team' : 'User'}
                        </span>
                        <span className="text-xs text-secondary-500">
                          {new Date(reply.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p
                        className={`whitespace-pre-wrap ${
                          reply.sender === 'admin' ? 'text-blue-800' : 'text-secondary-700'
                        }`}
                      >
                        {reply.message}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Admin Reply Box */}
              <div className="border-t border-secondary-200 pt-4">
                <h4 className="font-semibold text-secondary-900 mb-3">Reply as Admin</h4>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={4}
                  className="input-field w-full mb-3"
                  placeholder="Type your reply here..."
                />
                <button
                  onClick={() => handleReply(selectedTicket.ticket.id)}
                  disabled={replying || !replyMessage.trim()}
                  className="btn-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {replying ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminSupport;

